import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useProduct } from "@/components/pdp/PdpDataContext";

// PDP3.2-only slim sticky sub-nav. Pins to the TOP once the shopper scrolls
// past the hero/purchase area (the global header hides at the same moment,
// see RiversolHeader). Left: in-page section anchors with a scroll-spy
// underline. Right: full-height "Add to Bag" CTA. Colours inherit the v32
// teal swap from index.css.
const LINKS = [
  { label: "Details", target: "hero-section" },
  { label: "Video", target: "riversol-in-action" },
  { label: "What to Expect", target: "editorial-collage" },
  { label: "How to Use", target: "how-to-ritual-v31" },
  { label: "Ingredients", target: "actives-section" },
  { label: "The Riversol Difference", target: "benefits-why-blue" },
];

const BAR_H = 48;

// mobileCtaOnly (PDP3.4): on <lg hide the anchor links and stretch the CTA full-width, copy centered.
export const PdpStickyNav = ({ variant, mobileCtaOnly = false }) => {
  const P = useProduct();
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(LINKS[0].target);
  const price =
    P.variants?.find((x) => x.id === variant)?.price ||
    P.price?.regular ||
    P.variants?.[0]?.price;

  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector('[data-testid="hero-section"]');
      if (hero) setVisible(hero.getBoundingClientRect().bottom <= BAR_H);

      const offset = BAR_H + 8;
      let current = LINKS[0].target;
      for (const l of LINKS) {
        const el = document.querySelector(`[data-testid="${l.target}"]`);
        if (el && el.getBoundingClientRect().top <= offset) current = l.target;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollTo = (target) => {
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (!el) return;
    const y = window.scrollY + el.getBoundingClientRect().top - BAR_H + 1;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -70, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -70, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={`fixed top-0 inset-x-0 z-[60] bg-white/95 backdrop-blur-md border-neutral-200 shadow-[0_2px_20px_rgba(0,0,0,0.06)] ${mobileCtaOnly ? "lg:border-b" : "border-b"}`}
          data-testid="pdp-sticky-nav"
        >
          <div className={`w-full lg:pl-8 flex items-stretch justify-between gap-4 h-[48px] ${mobileCtaOnly ? "pl-0" : "pl-4"}`}>
            <nav
              className={`items-center gap-6 lg:gap-9 overflow-x-auto hide-scrollbar min-w-0 ${mobileCtaOnly ? "hidden lg:flex" : "flex"}`}
              style={{ scrollbarWidth: "none" }}
              data-testid="pdp-sticky-nav-links"
            >
              {LINKS.map((l) => {
                const isActive = active === l.target;
                return (
                  <button
                    key={l.target}
                    type="button"
                    onClick={() => scrollTo(l.target)}
                    data-testid={`pdp-nav-${l.target}`}
                    className={`relative shrink-0 h-full flex items-center text-[11px] lg:text-xs tracking-[0.14em] uppercase whitespace-nowrap transition-colors ${
                      isActive
                        ? "text-riversol-blue font-medium"
                        : "text-neutral-500 hover:text-riversol-blue"
                    }`}
                  >
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="pdp-nav-underline"
                        className="absolute left-0 right-0 bottom-0 h-[2px] bg-riversol-blue"
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      />
                    )}
                  </button>
                );
              })}
            </nav>
            <button
              type="button"
              onClick={() => scrollTo("hero-section")}
              data-testid="pdp-sticky-addtobag"
              className={`shrink-0 bg-riversol-blue text-white flex items-center gap-5 lg:gap-8 px-6 lg:px-8 hover:bg-riversol-blueLight transition-colors ${mobileCtaOnly ? "w-full lg:w-auto justify-center lg:justify-start" : ""}`}
            >
              <span className="text-[11px] lg:text-xs tracking-[0.12em] uppercase font-medium">
                {P.ctaLabel === "Add to cart" ? "Add to Bag" : P.ctaLabel}
              </span>
              {price && (
                <span
                  className="text-sm lg:text-base font-medium"
                  data-testid="pdp-sticky-price"
                >
                  {price}
                </span>
              )}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PdpStickyNav;
